export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Type for log data - allows common data types but not completely open
export type LogData = string | number | boolean | null | undefined | Record<string, unknown> | unknown[];

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: LogLevel;
  message: string;
  context?: string;
  data?: LogData;
  source: 'frontend' | 'backend' | 'network';
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private listeners: ((log: LogEntry) => void)[] = [];
  private enabled = true;
  private logLevel: LogLevel = 'info';
  private consoleEnabled = true;

  private logLevels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor() {
    // Check if we're in development mode
    if (typeof window !== 'undefined') {
      this.consoleEnabled = process.env.NODE_ENV === 'development';
      this.logLevel = (process.env.NEXT_PUBLIC_LOG_LEVEL as LogLevel) || 'info';
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enabled && this.logLevels[level] >= this.logLevels[this.logLevel];
  }

  private createLogEntry(level: LogLevel, message: string, context?: string, data?: LogData): LogEntry {
    return {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message,
      context,
      data,
      source: 'frontend',
    };
  }

  private log(level: LogLevel, message: string, context?: string, data?: LogData): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, message, context, data);
    
    // Add to internal logs
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output
    if (this.consoleEnabled) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
      const prefix = `[${level.toUpperCase()}] ${entry.timestamp.toISOString()}`;
      const contextStr = context ? ` [${context}]` : '';
      
      console[consoleMethod](`${prefix}${contextStr} ${message}`, data || '');
    }

    // Notify listeners
    this.listeners.forEach(listener => listener(entry));
  }

  debug(message: string, context?: string, data?: LogData): void {
    this.log('debug', message, context, data);
  }

  info(message: string, context?: string, data?: LogData): void {
    this.log('info', message, context, data);
  }

  warn(message: string, context?: string, data?: LogData): void {
    this.log('warn', message, context, data);
  }

  error(message: string, context?: string, data?: LogData): void {
    this.log('error', message, context, data);
  }

  // Subscribe to log updates
  subscribe(listener: (log: LogEntry) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Get all logs
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Clear logs
  clear(): void {
    this.logs = [];
  }

  // Configuration
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  setConsoleEnabled(enabled: boolean): void {
    this.consoleEnabled = enabled;
  }

  getConfig() {
    return {
      enabled: this.enabled,
      logLevel: this.logLevel,
      consoleEnabled: this.consoleEnabled,
    };
  }

  // Add network activity log
  logNetworkActivity(method: string, url: string, status?: number, duration?: number, error?: LogData): void {
    const message = `${method} ${url}${status ? ` - ${status}` : ''}${duration ? ` (${duration}ms)` : ''}`;
    const level: LogLevel = error || (status && status >= 400) ? 'error' : 'debug';
    
    this.log(level, message, 'Network', {
      method,
      url,
      status,
      duration,
      error: (error && typeof error === 'object' && 'message' in error) ? (error as unknown as Error).message : error,
    });
  }
}

// Create singleton instance
export const logger = new Logger();

// Export for use in React components
export default logger;